import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  FileText, Plus, ArrowLeft, Edit, Trash2, Save, X, Search
} from 'lucide-react';
import { toast } from 'sonner';

const PapersManagement = () => {
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPaper, setEditingPaper] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    board_id: '',
    subject_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [papersRes, boardsRes, subjectsRes] = await Promise.all([
        api.get('/question-bank/papers'),
        api.get('/boards'),
        api.get('/subjects')
      ]);
      setPapers(papersRes.data);
      setBoards(boardsRes.data);
      setSubjects(subjectsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingPaper ? 'Updating paper...' : 'Creating paper...');
    
    try {
      if (editingPaper) {
        await api.put(`/question-bank/papers/${editingPaper.id}`, formData);
        toast.dismiss(loadingToast);
        toast.success('Paper updated successfully');
      } else {
        await api.post('/question-bank/papers', formData);
        toast.dismiss(loadingToast);
        toast.success('Paper created successfully');
      }
      setShowModal(false);
      setEditingPaper(null);
      setFormData({ code: '', name: '', board_id: '', subject_id: '', description: '' });
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to save paper');
    }
  };

  const handleEdit = (paper) => {
    setEditingPaper(paper);
    setFormData({
      code: paper.code || '',
      name: paper.name || '',
      board_id: paper.board_id || '',
      subject_id: paper.subject_id || '',
      description: paper.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (paperId, paperName) => {
    if (!window.confirm(`Are you sure you want to delete "${paperName}"?`)) return;
    
    try {
      await api.delete(`/question-bank/papers/${paperId}`);
      toast.success('Paper deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete paper');
    }
  };

  const getBoardName = (boardId) => boards.find(b => b.id === boardId)?.name || 'Unknown';
  const getSubjectName = (subjectId) => subjects.find(s => s.id === subjectId)?.name || 'Unknown';

  const filteredPapers = papers.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading papers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/qbm/dashboard')}
            className="flex items-center gap-2 text-blue-100 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Question Bank
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <FileText className="w-8 h-8" />
                Papers / Components
              </h1>
              <p className="mt-2 text-blue-200">Manage examination papers and components</p>
            </div>
            <button
              onClick={() => {
                setEditingPaper(null);
                setFormData({ code: '', name: '', board_id: '', subject_id: '', description: '' });
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-white text-blue-700 rounded-lg font-medium hover:bg-blue-50 transition-all"
              data-testid="create-paper-btn"
            >
              <Plus className="w-5 h-5" />
              Add Paper
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search papers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Papers Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Code</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Board</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Subject</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPapers.map(paper => (
                <tr key={paper.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">
                      {paper.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-medium text-slate-900">{paper.name}</td>
                  <td className="px-6 py-4 text-slate-600">{getBoardName(paper.board_id)}</td>
                  <td className="px-6 py-4 text-slate-600">{getSubjectName(paper.subject_id)}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(paper)}
                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(paper.id, paper.name)}
                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPapers.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No papers found</p>
                    <p className="text-sm mt-1">Create a new paper to get started</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                {editingPaper ? 'Edit Paper' : 'Create New Paper'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Paper Code</label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., P1, P2, P3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Paper Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Paper 1 - Theory"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                  <select
                    required
                    value={formData.board_id}
                    onChange={(e) => setFormData({ ...formData, board_id: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Board</option>
                    {boards.map(board => (
                      <option key={board.id} value={board.id}>{board.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                  <select
                    required
                    value={formData.subject_id}
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Brief description of this paper..."
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingPaper(null); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all"
                >
                  <Save className="w-4 h-4" />
                  {editingPaper ? 'Save Changes' : 'Create Paper'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PapersManagement;
